import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import StatusPill from '../../components/StatusPill';
import { CodeChip } from '../../components/ui/CountrySelect';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, StatCard } from '../../components/ui/Page';
import { ago } from '../../lib/format';
import { CATEGORIES } from './Country';

type CountryRow = {
  code: string;
  name: string;
  total: number;
  active: number;
  required: number;
  updatedAt: string;
  hasPrompt: boolean;
  companies: number;
  groups?: Record<string, number>;
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi<{ countries: CountryRow[] }>('/admin/documents');
  const [q, setQ] = useState('');
  const countries = data?.countries || [];
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return countries.filter((c) => !t || c.name.toLowerCase().includes(t) || c.code.toLowerCase().includes(t));
  }, [countries, q]);

  const totalDocs = countries.reduce((n, c) => n + c.active, 0);

  return (
    <div>
      <PageHeader
        title="Documents"
        description="Country checklists grouped into Financial, Ownership & legal, Individuals & KYC, and Business. When a rating runs, one agent validates each group in parallel, then results are aggregated into the summary."
        actions={
          <Link to="/documents/new" className="mz-btn-primary">
            + Add country
          </Link>
        }
      />

      <div className="mz-fade-up grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard label="Countries" value={countries.length} />
        <StatCard label="Active documents" value={totalDocs} />
        <StatCard label="Without rating prompt" value={countries.filter((c) => !c.hasPrompt).length} accent={countries.some((c) => !c.hasPrompt)} />
      </div>

      <Card className="mz-fade-up mz-d1 mt-4" padded={false}>
        <div className="border-b border-[#fbeef4] p-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search country…" className="max-w-sm" />
        </div>
        {error && (
          <div className="px-3 pt-3">
            <Notice tone="error">{error}</Notice>
          </div>
        )}
        {loading && !data ? (
          <LoadingRows />
        ) : shown.length === 0 ? (
          <EmptyState
            title={countries.length ? 'No country matches' : 'No countries configured'}
            description="Add a country to start from the standard Micus credit pack."
            action={<Link to="/documents/new" className="mz-btn-primary">+ Add country</Link>}
          />
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="mz-table w-full">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Groups</th>
                  <th>Documents</th>
                  <th>Required</th>
                  <th>Companies</th>
                  <th>Rating prompt</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={c.code} className="mz-row-link" onClick={() => navigate(`/documents/${c.code}`)}>
                    <td>
                      <div className="flex items-center gap-2">
                        <CodeChip code={c.code} />
                        <span className="font-semibold">{c.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {CATEGORIES.map((g) => {
                          const n = c.groups?.[g.value] || 0;
                          return n > 0 ? <StatusPill key={g.value} label={`${g.label.split(' ')[0]} ${n}`} tone="slate" /> : null;
                        })}
                        {!CATEGORIES.some((g) => (c.groups?.[g.value] || 0) > 0) && <span className="text-xs text-[#a77d92]">—</span>}
                      </div>
                    </td>
                    <td>
                      <span className="font-semibold">{c.active}</span>
                      {c.total !== c.active && <span className="text-xs text-[#a77d92]"> of {c.total}</span>}
                    </td>
                    <td className="text-[#9a6b82]">{c.required}</td>
                    <td className="text-[#9a6b82]">{c.companies}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Link to={`/ratings/${c.code}`} className="inline-flex">
                        <StatusPill label={c.hasPrompt ? 'Configured' : 'Set up'} tone={c.hasPrompt ? 'green' : 'amber'} />
                      </Link>
                    </td>
                    <td className="whitespace-nowrap text-xs text-[#9a6b82]">{ago(c.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
